// Helpers sobre la configuración de Campos de una capa (paso 2 del
// formulario ArcGIS, FormularioArcgisPaso2.vue → models/layer.go). Comparten
// la interpretación de visible/order/alias/titulo entre el popup de consulta
// (selectService.js) y el detalle tabular del geoproceso
// (GeoprocessingTable.vue) para QA-206/QA-207.

// Identificadores internos de los servicios (con sufijos variables como
// OBJECTID_12 o FID_EDUCAC) y columnas de bookkeeping del geoproceso que no
// aportan al lector del detalle tabular.
const TECHNICAL_KEY_PATTERN = /^(objectid|fid|shape)([_.].*|\(.*)?$/i;
const TECHNICAL_KEYS = new Set(["geom", "the_geom", "layer_id", "layer_name", "geometry", "_radius", "_report_geometry"]);

export function isTechnicalResultKey(key) {
  return TECHNICAL_KEY_PATTERN.test(key) || TECHNICAL_KEYS.has(String(key).toLowerCase());
}

// visibleConfiguredFields: filas visibles con nombre, ordenadas por `order`.
export function visibleConfiguredFields(campos) {
  if (!Array.isArray(campos)) return [];
  return campos
    .filter(campo => campo && campo.visible && campo.name)
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

// configuredTitle: valor del primer campo marcado `titulo` (visible, según
// order) cuyo valor en properties sea escalar y no vacío; null si ninguno.
export function configuredTitle(properties, campos) {
  if (!properties) return null;
  for (const campo of visibleConfiguredFields(campos)) {
    if (!campo.titulo) continue;
    const value = properties[campo.name];
    if (value === null || value === undefined) continue;
    if (typeof value === 'object' || typeof value === 'function') continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return null;
}

/**
 * configuredColumns: dadas las keys reales de las filas y la configuración de
 * Campos, devuelve las columnas [{ name, label }] visibles en el orden
 * configurado y con su alias como etiqueta. Las keys sin fila de
 * configuración (p. ej. "Radio (m)", que agrega el backend del geoproceso) se
 * conservan al final con su nombre crudo.
 *
 * `visible` se guarda con omitempty (models/layer.go): una configuración
 * antigua llega sin la clave, y filtrar a rajatabla dejaría la tabla vacía —
 * si ninguna fila visible calza con las keys, se devuelven las keys tal cual.
 * Mismo fallback que applyLayerFields en selectService.js.
 */
export function configuredColumns(keys, campos) {
  const identity = (keys || []).map(name => ({ name, label: name }));
  const visibles = visibleConfiguredFields(campos).filter(campo => keys.includes(campo.name));
  if (!visibles.length) return identity;

  const configuredNames = new Set(campos.filter(campo => campo && campo.name).map(campo => campo.name));
  const used = new Set();
  const columns = [];
  for (const campo of visibles) {
    if (used.has(campo.name)) continue;
    used.add(campo.name);
    // Alias vacío o repetido cae al nombre crudo: dos alias iguales serían
    // dos encabezados indistinguibles en la tabla.
    const label = campo.alias && !columns.some(column => column.label === campo.alias) ? campo.alias : campo.name;
    columns.push({ name: campo.name, label });
  }
  for (const name of keys) {
    if (!configuredNames.has(name) && !used.has(name)) {
      used.add(name);
      columns.push({ name, label: name });
    }
  }
  return columns;
}
